import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { adminStatsAPI } from '../services/adminApi';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminStatsAPI.get();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      if (error.response?.status === 403) {
        alert('Keine Admin-Berechtigung!');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                👑 Admin Panel
              </h1>
              <nav className="flex space-x-4">
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate('/admin/packages')}
                  className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  Pakete
                </button>
                <button
                  onClick={() => navigate('/admin/courses')}
                  className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  Kurse
                </button>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  User
                </button>
               <button
  onClick={() => navigate('/admin/emails')}
  className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
>
  Email-Vorlagen
</button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                User-Ansicht
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Übersicht</h2>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-primary-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Gesamt User</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.users || 0}</p>
              </div>
              <div className="text-4xl">👥</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-secondary-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Aktive Pakete</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.packages || 0}</p>
              </div>
              <div className="text-4xl">📦</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Aktive Kurse</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.courses || 0}</p>
              </div>
              <div className="text-4xl">📚</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Gesamt Lektionen</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.lessons || 0}</p>
              </div>
              <div className="text-4xl">🎥</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Schnellaktionen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/admin/packages')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
            >
              <div className="text-3xl mb-2">📦</div>
              <h4 className="font-bold text-gray-900">Neues Paket</h4>
              <p className="text-sm text-gray-600 mt-1">Erstelle ein neues Lernpaket</p>
            </button>

            <button
              onClick={() => navigate('/admin/courses')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
            >
              <div className="text-3xl mb-2">📚</div>
              <h4 className="font-bold text-gray-900">Neuer Kurs</h4>
              <p className="text-sm text-gray-600 mt-1">Füge einen neuen Kurs hinzu</p>
            </button>

            <button
              onClick={() => navigate('/admin/users')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
            >
              <div className="text-3xl mb-2">👥</div>
              <h4 className="font-bold text-gray-900">User verwalten</h4>
              <p className="text-sm text-gray-600 mt-1">Pakete zuweisen und mehr</p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
